import { Engine } from "json-rules-engine"

import {
    EngineFacts,
    Facts,
    Identifier,
    MetaFacts,
    Question,
    Result,
    Schema,
    Subquestion,
} from "./schema"

type FullResult = {
    result: Result[]
    metaFacts: MetaFacts
}

export function make(schema: Schema) {
    const rules = schema.results.map((result: Result) => ({
        conditions: result.when,
        event: {
            type: result.id,
        },
    }))
    const subquestionRules = schema.questions.flatMap((question) =>
        question.subquestions.map((subquestion) => ({
            conditions: subquestion.when,
            event: {
                type: subquestion.id,
            },
        })),
    )
    const options = {
        allowUndefinedFacts: true,
    }
    const engine = new Engine([...rules, ...subquestionRules], options)

    async function innerRun(facts: EngineFacts): Promise<Identifier[]> {
        const { events } = await engine.run(facts)
        return events.map((resultEvent) => resultEvent.type as Identifier)
    }

    async function metaFacts(facts: Facts): Promise<MetaFacts> {
        const matchedIds = await innerRun(facts)

        const lacksAnswer = ({ id }: { id: Identifier; [_key: string]: any }) =>
            !facts[id]

        const $activeSubQuestions: Identifier[] = schema.questions.flatMap(
            (question: Question) =>
                question.subquestions
                    .filter((subquestion: Subquestion) =>
                        matchedIds.includes(subquestion.id),
                    )
                    .map((subquestion: Subquestion) => subquestion.id),
        )

        const unansweredQuestion: Question | undefined = schema.questions.find(
            (question: Question) => {
                if (lacksAnswer(question)) return true

                const activeSubquestions: Subquestion[] =
                    question.subquestions.filter((sq: Subquestion) =>
                        $activeSubQuestions.includes(sq.id),
                    )

                const unansweredActiveSub: Subquestion | undefined =
                    activeSubquestions.find((sq: Subquestion) =>
                        lacksAnswer(sq),
                    )

                return !!unansweredActiveSub
            },
        )

        return {
            $nextQuestion: unansweredQuestion?.id ?? null,
            $activeSubQuestions,
        }
    }
    async function run(facts: Facts): Promise<FullResult> {
        const localMetaFacts = await metaFacts(facts)
        const allFacts = {
            ...facts,
            ...localMetaFacts,
        }

        const matchedIds = await innerRun(allFacts)

        return {
            result: readResults(schema.results, matchedIds),
            metaFacts: localMetaFacts,
        }
    }

    return {
        run,
        async evaluate(facts: Facts): Promise<Result[]> {
            const { result } = await run(facts)
            return result
        },
        metaFacts,
    }
}

function readResults(results: readonly Result[], ids: Identifier[]): Result[] {
    return results.filter((result) => ids.includes(result.id))
}
